import {
  Button,
  Icon,
  Modal,
  Text,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ButtonGroup,
  Heading,
  ModalBody,
  LinkButton,
  useTheme
} from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { HOMEOWNER_DIGEST_INTRO_MODULE } from '../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'
import { selectCompanyName } from '../../../../store/selectors/customerProfile'
import { useSelector } from 'react-redux'
import { LeavingHomebotIllustration } from './LeavingHomebotIllustration'
import styles from './LeavingHomebotModal.module.scss'

export const MSG = defineMessages({
  leavingHomebotHeader: {
    id: 'LeavingHomebotModal.header',
    defaultMessage: 'Review your financing options'
  },
  youreOneStepCloser: {
    id: 'LeavingHomebotModal.subHeader',
    defaultMessage:
      "You're one step closer towards buying a new home, refinancing, or getting cash from your home's equity"
  },
  corporationNameOffersHomeFinancingOptions: {
    id: 'LeavingHomebotModal.corporationNameOffersHomeFinancingOptions',
    defaultMessage: '{corporationName} offers home financing options.'
  },
  byContinuing: {
    id: 'LeavingHomebotModal.byContinuing',
    defaultMessage: "By continuing, you'll go to their site in a new tab."
  },
  legalSmallPrint: {
    id: 'LeavingHomebotModal.legalSmallPrint',
    defaultMessage:
      'Homebot bears no responsibility for the accuracy, legality, or content of the external site. Contact {corporationName} for answers to questions regarding its content.'
  },
  maybeLater: {
    id: 'LeavingHomebotModal.maybeLater',
    defaultMessage: 'Maybe later'
  },
  goToLender: {
    id: 'LeavingHomebotModal.goToLender',
    defaultMessage: 'Go to lender'
  }
})
interface LeavingHomebotModalProps {
  isOpen: boolean
  ctaLinkUrl: string
  handleClose: () => void
}

export const LeavingHomebotModal = ({ isOpen, ctaLinkUrl, handleClose }: LeavingHomebotModalProps) => {
  const corporationName = useSelector(selectCompanyName)
  const intl = useIntl()
  const { theme } = useTheme()

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading textAlign='left' as='h2' mb={0}>
            {intl.formatMessage(MSG.leavingHomebotHeader)}
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <LeavingHomebotIllustration />
        <ModalBody>
          <Text fontWeight='semibold' mb={4}>
            {intl.formatMessage(MSG.youreOneStepCloser)}
          </Text>
          <Text mb={4}>
            {intl.formatMessage(MSG.corporationNameOffersHomeFinancingOptions, {
              corporationName: corporationName
            })}
          </Text>
          <Text mb={6}>{intl.formatMessage(MSG.byContinuing)}</Text>
          <Text size='3xs'>
            {intl.formatMessage(MSG.legalSmallPrint, {
              corporationName: corporationName
            })}
          </Text>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup
            spacing={3}
            className={
              // typography.scss sets global anchor styles that were overriding the design system "link styled as button" styles
              // here we have to re-override the global styles to match the design system styles
              theme.isLightThemeType ? styles.anchorOverrideWhite : styles.anchorOverrideBlack
            }
          >
            <Button
              onClick={handleClose}
              colorScheme='primary'
              variant='ghost'
              tracking={{
                guid: '2wU6vXf8a6nSijoDHDU53d',
                ui_context: HOMEOWNER_DIGEST_INTRO_MODULE,
                descriptor: 'newrez link clicked from leave homebot modal'
              }}
            >
              {intl.formatMessage(MSG.maybeLater)}
            </Button>
            <LinkButton
              isExternal
              rightIcon={<Icon name='external-link' width={3} height={3} />}
              href={ctaLinkUrl}
              colorScheme='primary'
              tracking={{
                guid: '9rHnbW6rrREo63jtrXNxhB',
                ui_context: HOMEOWNER_DIGEST_INTRO_MODULE,
                descriptor: 'user dismissed the leave homebot modal'
              }}
            >
              {intl.formatMessage(MSG.goToLender)}
            </LinkButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
